import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { serviceAPI } from '../lib/api';
import { Users, DollarSign, TrendingUp, Link2, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function AffiliatePage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await serviceAPI.request({ ...form, service_type: 'affiliate_program' });
      setSubmitted(true);
      toast.success('Application submitted!');
    } catch { toast.error('Failed to submit'); }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="px-6 md:px-12 max-w-7xl mx-auto py-20">
        <div className="max-w-lg mx-auto bg-[#0A0A0A] border border-white/5 rounded-sm p-12 text-center">
          <CheckCircle className="h-12 w-12 text-[#00FF94] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Application Received!</h3>
          <p className="text-zinc-500">We'll review your application and send you affiliate details within 48 hours.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="affiliate-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Users className="h-10 w-10 text-[#00E5FF] mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('services.affiliate') || 'Affiliate Program'}</h1>
          <p className="text-zinc-500 mt-3 max-w-xl mx-auto">Earn commissions by referring domain buyers and sellers to XDomainX.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {[
            { icon: DollarSign, title: 'Up to 15% Commission', desc: 'Earn generous commissions on every successful sale through your referral link.' },
            { icon: Link2, title: 'Easy Tracking', desc: 'Get your unique referral link and track all conversions in real-time.' },
            { icon: TrendingUp, title: 'Monthly Payouts', desc: 'Reliable monthly payments via bank transfer or PayPal.' },
          ].map((item, i) => (
            <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <item.icon className="h-8 w-8 text-[#00E5FF] mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{item.title}</h3>
              <p className="text-sm text-zinc-500">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 mb-8">
          <h3 className="text-lg font-semibold text-white font-['Outfit'] mb-4">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: '01', title: 'Apply', desc: 'Fill out the application form below' },
              { step: '02', title: 'Get Approved', desc: 'We review and approve within 48 hours' },
              { step: '03', title: 'Share', desc: 'Share your unique referral link' },
              { step: '04', title: 'Earn', desc: 'Earn commissions on every sale' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-black text-[#00E5FF]/20 font-['Outfit']">{s.step}</div>
                <div className="text-sm font-semibold text-white mt-1">{s.title}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 space-y-5" data-testid="affiliate-form">
          <h3 className="text-lg font-semibold text-white font-['Outfit']">Apply Now</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.name')}</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="affiliate-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="affiliate-email" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tell us about yourself</Label>
            <Textarea value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} rows={3}
              className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none" placeholder="Website, social media, how you plan to promote..." data-testid="affiliate-details" />
          </div>
          <Button type="submit" disabled={loading}
            className="h-11 px-8 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="affiliate-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Apply Now <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        </form>
      </div>
    </div>
  );
}
